import React, { useRef, useState } from 'react';
import { Delivery } from 'typings/models';
import {
    Card,
    CardContent,
    Typography,
    Button,
    ButtonGroup,
    Box,
    Grid2 as Grid,
    Popper,
    Grow,
    Paper,
    ClickAwayListener,
    MenuList,
    MenuItem,
    Modal,
    TextField,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
} from '@mui/material';
import { ArrowDropDown, Delete, CloudUpload } from '@mui/icons-material';
import './DeliveryCard.scss';
import { useTravelContext } from 'misc/TravelContext';
import FinishedDeliveryCard from './FinishedDeliveryCard';
import { reportDeliveryProblem, completeDelivery, reopenDelivery, uploadFileViaBackend, FileRef } from 'api/deliveries';
import CustomButton from 'custom/buttons/CustomButton';

interface DeliveryCardProps {
    delivery: Delivery;
    onRegisterDelivery: (deliveryId: string) => void;
}

interface SelectedFile {
    file: File;
    progress: number;
    uploaded: boolean;
    s3Key?: string;
    error?: string;
    retrying?: boolean;
}

const DeliveryCard: React.FC<DeliveryCardProps> = ({ delivery, onRegisterDelivery }) => {
    const { updateDelivery, commentCodes } = useTravelContext();

    // ── Completion upload modal ──────────────────────────────────────────────
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [completionFiles, setCompletionFiles] = useState<SelectedFile[]>([]);
    const completionFileRef = useRef<HTMLInputElement>(null);

    // ── Problem upload modal ─────────────────────────────────────────────────
    const [openProblemModal, setOpenProblemModal] = useState(false);
    const [pendingProblem, setPendingProblem] = useState<{ comment_code_id: string; isOther: boolean } | null>(null);
    const [problemComment, setProblemComment] = useState('');
    const [problemFiles, setProblemFiles] = useState<SelectedFile[]>([]);
    const problemFileRef = useRef<HTMLInputElement>(null);

    // ── Shared upload state ──────────────────────────────────────────────────
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    // ── Dropdown menu ────────────────────────────────────────────────────────
    const [openMenu, setOpenMenu] = useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = React.useState(1);

    const handleToggle = () => setOpenMenu((prev) => !prev);
    const handleClose = (event: Event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) return;
        setOpenMenu(false);
    };

    // ── Helpers ──────────────────────────────────────────────────────────────
    const buildSelectedFiles = (prev: SelectedFile[], newFiles: File[]) =>
        [...prev, ...newFiles.map((f) => ({ file: f, progress: 0, uploaded: false }))];

    const uploadFiles = async (files: SelectedFile[], fileType: string, setFiles: React.Dispatch<React.SetStateAction<SelectedFile[]>>): Promise<FileRef[]> => {
        const refs: FileRef[] = [];
        for (let i = 0; i < files.length; i++) {
            const sf = files[i];
            const result = await uploadFileViaBackend(
                delivery.id,
                sf.file,
                (pct) => setFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, progress: pct } : f))
            );
            setFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, uploaded: true, progress: 100, s3Key: result.s3_key } : f));
            refs.push({ s3_key: result.s3_key, filename: result.filename, content_type: result.content_type, file_type: fileType });
        }
        return refs;
    };

    // ── Completion flow ──────────────────────────────────────────────────────
    const handleOpenUploadModal = () => {
        setCompletionFiles([]);
        setUploadError(null);
        setOpenUploadModal(true);
    };

    const handleCompletionFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        setCompletionFiles((prev) => buildSelectedFiles(prev, Array.from(e.target.files!)));
        e.target.value = '';
    };

    const handleUploadAndComplete = async () => {
        if (completionFiles.length === 0) { setUploadError('Sube al menos 1 archivo.'); return; }
        setUploading(true);
        setUploadError(null);
        try {
            const refs = await uploadFiles(completionFiles, 'completion', setCompletionFiles);
            const response = await completeDelivery(delivery.id, 'Entregado', refs);
            updateDelivery(response);
            setOpenUploadModal(false);
        } catch (err: any) {
            setUploadError(err?.message || 'Error al subir los archivos.');
        } finally {
            setUploading(false);
        }
    };

    // ── Problem flow ─────────────────────────────────────────────────────────
    const openProblemUploadModal = (comment_code_id: string, isOther = false) => {
        setPendingProblem({ comment_code_id, isOther });
        setProblemFiles([]);
        setProblemComment('');
        setUploadError(null);
        setOpenMenu(false);
        setOpenProblemModal(true);
    };

    const handleProblemFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        setProblemFiles((prev) => buildSelectedFiles(prev, Array.from(e.target.files!)));
        e.target.value = '';
    };

    const handleUploadAndReportProblem = async () => {
        if (problemFiles.length === 0) { setUploadError('Sube al menos 1 foto.'); return; }
        if (!pendingProblem) return;
        setUploading(true);
        setUploadError(null);
        try {
            const refs = await uploadFiles(problemFiles, 'problem', setProblemFiles);
            const response = await reportDeliveryProblem(
                delivery.id, 'Failed',
                pendingProblem.isOther ? problemComment : '',
                pendingProblem.comment_code_id,
                refs,
            );
            updateDelivery(response);
            setOpenProblemModal(false);
        } catch (err: any) {
            setUploadError(err?.message || 'Error al subir los archivos.');
        } finally {
            setUploading(false);
        }
    };

    // ── Dropdown options ─────────────────────────────────────────────────────
    const extraButtonOptions: { label: string; onClick: () => void }[] = [];
    commentCodes.forEach((cc) => {
        if (cc.code === 'OTHER') return;
        extraButtonOptions.push({
            label: cc.short_description,
            onClick: () => openProblemUploadModal(cc.id),
        });
    });
    extraButtonOptions.push({
        label: 'Otro',
        onClick: () => openProblemUploadModal('7', true),
    });

    // ── Shared file list renderer ─────────────────────────────────────────────
    const renderFileList = (files: SelectedFile[], onRemove: (i: number) => void) => (
        <List dense>
            {files.map((sf, i) => (
                <ListItem key={i} sx={{ pl: 0 }}>
                    <ListItemText
                        primary={sf.file.name}
                        secondary={sf.uploaded ? '✓ Subido' : sf.retrying ? 'Reintentando...' : sf.progress > 0 ? `${sf.progress}%` : 'Pendiente'}
                        secondaryTypographyProps={{ color: sf.uploaded ? 'success.main' : sf.retrying ? 'warning.main' : 'text.secondary' }}
                    />
                    {sf.progress > 0 && !sf.uploaded && (
                        <Box sx={{ width: 80, mr: 1 }}>
                            <LinearProgress variant="determinate" value={sf.progress} />
                        </Box>
                    )}
                    <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => onRemove(i)} disabled={uploading} size="small">
                            <Delete fontSize="small" />
                        </IconButton>
                    </ListItemSecondaryAction>
                </ListItem>
            ))}
        </List>
    );

    // ── Delivery description ──────────────────────────────────────────────────
    const deliveryDescription = (
        <>
            <Typography variant="h6" component="div" gutterBottom className="delivery-card__title">
                {delivery.order.id}
            </Typography>
            <Typography variant="body1" gutterBottom>{delivery.order.client.name}</Typography>
            <Typography variant="body2" color="text.secondary">{delivery.order.client.address}</Typography>
        </>
    );

    const optionsPopper = (
        <Popper className="delivery-card__popper" open={openMenu} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
            {({ TransitionProps, placement }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                    <Paper>
                        <ClickAwayListener onClickAway={handleClose}>
                            <MenuList id="split-button-menu" autoFocusItem>
                                {extraButtonOptions.map((option, index) => (
                                    <MenuItem key={`${delivery.id}-${index}`} selected={index === selectedIndex} onClick={option.onClick}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );

    const buttonGroup = (
        <ButtonGroup variant="contained" ref={anchorRef}>
            <CustomButton onClick={handleOpenUploadModal} className="delivery-card__button">
                Registrar llegada
            </CustomButton>
            <CustomButton
                type='error'
                aria-controls={openMenu ? 'split-button-menu' : undefined}
                aria-expanded={openMenu ? 'true' : undefined}
                aria-haspopup="menu"
                onClick={handleToggle}
            >
                <ArrowDropDown />
            </CustomButton>
        </ButtonGroup>
    );

    // ── Modals ────────────────────────────────────────────────────────────────
    const uploadModal = (
        <Modal open={openUploadModal} onClose={() => !uploading && setOpenUploadModal(false)} className="delivery-card__modal">
            <Box className="delivery-card__modal-content">
                <Typography variant="h6" gutterBottom>Subir comprobantes</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Sube al menos 1 foto o PDF para registrar la llegada.
                </Typography>
                <Box sx={{ mt: 1, mb: 1 }}>
                    <input ref={completionFileRef} type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }} onChange={handleCompletionFileChange} />
                    <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => completionFileRef.current?.click()} disabled={uploading}>
                        Seleccionar archivos ({completionFiles.length})
                    </Button>
                </Box>
                {completionFiles.length > 0 && renderFileList(completionFiles, (i) => setCompletionFiles((p) => p.filter((_, idx) => idx !== i)))}
                {uploadError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{uploadError}</Typography>}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setOpenUploadModal(false)} disabled={uploading}>Cancelar</Button>
                    <Button variant="contained" onClick={handleUploadAndComplete} disabled={uploading || completionFiles.length === 0}>
                        {uploading ? 'Subiendo...' : 'Subir y completar'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );

    const problemModal = (
        <Modal open={openProblemModal} onClose={() => !uploading && setOpenProblemModal(false)} className="delivery-card__modal">
            <Box className="delivery-card__modal-content">
                <Typography variant="h6" gutterBottom>Reportar problema</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Sube al menos 1 foto como evidencia del problema.
                </Typography>
                {pendingProblem?.isOther && (
                    <TextField
                        fullWidth multiline rows={3}
                        placeholder="Describe el problema..."
                        variant="outlined" margin="dense"
                        value={problemComment}
                        onChange={(e) => setProblemComment(e.target.value)}
                        disabled={uploading}
                    />
                )}
                <Box sx={{ mt: 1, mb: 1 }}>
                    <input ref={problemFileRef} type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }} onChange={handleProblemFileChange} />
                    <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => problemFileRef.current?.click()} disabled={uploading}>
                        Seleccionar fotos ({problemFiles.length})
                    </Button>
                </Box>
                {problemFiles.length > 0 && renderFileList(problemFiles, (i) => setProblemFiles((p) => p.filter((_, idx) => idx !== i)))}
                {uploadError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{uploadError}</Typography>}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setOpenProblemModal(false)} disabled={uploading}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={handleUploadAndReportProblem} disabled={uploading || problemFiles.length === 0}>
                        {uploading ? 'Subiendo...' : 'Subir y reportar'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );

    if (delivery.status !== 'In Progress') {
        return <FinishedDeliveryCard delivery={delivery} />;
    }

    return (
        <>
            <Card className="delivery-card">
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>{deliveryDescription}</Grid>
                        <Grid size={{ xs: 12, sm: 6 }} className="delivery-card__grid-container">
                            {buttonGroup}
                            {optionsPopper}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {uploadModal}
            {problemModal}
        </>
    );
};

export default DeliveryCard;
